import api from '../config/axiosConfig';
import { sessionManager } from '../config/sessionManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/** Codes from validate-session / auth that require immediate logout + login redirect */
const BLOCKING_SESSION_CODES = ['ACCOUNT_SUSPENDED_PAYMENT', 'CLIENT_ARCHIVED', 'ACCOUNT_INACTIVE'] as const;

type BlockingCode = (typeof BLOCKING_SESSION_CODES)[number];

function isBlockingSessionPayload(data: unknown): data is {
  code: BlockingCode;
  client?: { name?: string };
} {
  if (!data || typeof data !== 'object' || !('code' in data)) return false;
  const code = (data as { code: string }).code;
  return (BLOCKING_SESSION_CODES as readonly string[]).includes(code);
}

class SessionValidator {
  private intervalId: NodeJS.Timeout | null = null;
  private readonly VALIDATION_INTERVAL = 5 * 60 * 1000; // 5 minutes

  /**
   * Start background session validation
   * Checks every 5 minutes if permissions have changed
   */
  start(): void {
    if (this.intervalId) {
      console.warn('Session validator already running');
      return;
    }

    this.intervalId = setInterval(() => {
      this.validateSession();
    }, this.VALIDATION_INTERVAL);

    // Run immediately on start (if needed)
    if (sessionManager.needsValidation()) {
      this.validateSession();
    }
  }

  /**
   * Stop background session validation
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Match Login page / axios: clear session and hard-navigate so ProtectedRoute and App state reset.
   */
  private forceLogoutAndRedirectToLogin(code: BlockingCode, clientName?: string): void {
    this.stop();
    sessionManager.clearSession();

    const url = new URL(window.location.href);
    url.pathname = '/login';
    url.search = '';
    url.hash = '';

    if (code === 'ACCOUNT_SUSPENDED_PAYMENT') {
      url.searchParams.set('suspended', '1');
    } else if (code === 'CLIENT_ARCHIVED') {
      if (clientName) {
        sessionStorage.setItem('archivedClientName', clientName);
      }
      url.searchParams.set('archived', '1');
    } else if (code === 'ACCOUNT_INACTIVE') {
      url.searchParams.set('inactive', '1');
    }

    window.location.replace(url.toString());
  }

  /**
   * Manually trigger session validation
   */
  async validateSession(): Promise<boolean> {
    try {
      if (!sessionManager.isAuthenticated()) {
        return false;
      }

      const user = sessionManager.getUser();
      if (!user) {
        return false;
      }

      // Call validation endpoint
      const response = await api.post(`${API_URL}/auth/user/validate-session`, {
        userId: user.id,
      });

      const payload = response.data;

      // HTTP 200 with success:false — axios won't run 403 interceptors; must logout here
      if (!payload.success && isBlockingSessionPayload(payload)) {
        this.forceLogoutAndRedirectToLogin(payload.code, payload.client?.name);
        return false;
      }

      if (payload.success && payload.user) {
        const serverUser = payload.user;
        const currentPermissions = sessionManager.getUserPermissions();
        const currentRole = sessionManager.getUserRole();

        // Check if role has changed
        const roleChanged = currentRole?.value !== serverUser.role?.value;

        // Check if permissions have changed
        const permissionsChanged = JSON.stringify(currentPermissions) !== JSON.stringify(serverUser.permissions);

        if (roleChanged || permissionsChanged) {
          console.log('⚠️ User role or permissions have been updated');
          console.log('  Role changed:', roleChanged ? `${currentRole?.value} → ${serverUser.role?.value}` : 'No');
          console.log('  Permissions changed:', permissionsChanged ? 'Yes' : 'No');

          // Update session with new role and permissions
          const currentSession = sessionManager.getSession();
          if (currentSession) {
            sessionManager.updateSession({
              ...currentSession,
              user: {
                ...currentSession.user,
                role: serverUser.role,
                permissions: serverUser.permissions,
                customPermissions: serverUser.customPermissions,
              },
              lastValidated: Date.now(),
            });
          }

          // Notify user about changes
          this.notifyPermissionChange();

          // Reload the page to apply new role/permissions
          window.location.reload();
        } else {
          // Just update the last validated timestamp
          const currentSession = sessionManager.getSession();
          if (currentSession) {
            sessionManager.updateSession({
              ...currentSession,
              lastValidated: Date.now(),
            });
          }
        }

        return true;
      }

      return false;
    } catch (error: unknown) {
      const data = (error as { response?: { data?: unknown } })?.response?.data;
      if (isBlockingSessionPayload(data)) {
        this.forceLogoutAndRedirectToLogin(data.code, data.client?.name);
        return false;
      }
      console.error('Session validation error:', error);
      return false;
    }
  }

  /**
   * Notify user about permission changes
   */
  private notifyPermissionChange(): void {
    // Create a custom event for permission changes
    window.dispatchEvent(
      new CustomEvent('permissionsChanged', {
        detail: {
          message: 'Your permissions have been updated by an administrator',
          timestamp: Date.now(),
        },
      }),
    );
  }
}

// Export singleton instance
export const sessionValidator = new SessionValidator();

// Auto-start validation when user is authenticated
if (sessionManager.isAuthenticated()) {
  sessionValidator.start();
}
